/**
 * Invite acceptance — `/einladung?token=…`.
 *
 * The link creates the account and the membership and then signs the reader in,
 * so it is redeemed by POST from here, never by the mail client fetching a URL.
 *
 * The three states the brief names — widerrufen, bereits eingelöst, abgelaufen
 * — all come back as 400 with the backend's sentence. That sentence is shown
 * verbatim (it already says what to do), plus two concrete ways forward: ask
 * the sender for a new invite, or sign in if the account already exists. The
 * frontend deliberately does not try to classify the message by matching its
 * text — the copy would drift and the classification would silently rot.
 */
import { useEffect, useRef, useState } from 'react'
import { CircleAlert, LoaderCircle, LogIn } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { useSession } from '@/features/auth/SessionContext'
import { redeemOnce } from '@/features/auth/redeem'
import { useI18n } from '@/i18n'
import { ROUTES, buildPath, navigate, useRouteMatch } from '@/lib/router'

export function InviteScreen() {
  const { t } = useI18n()
  const route = useRouteMatch()
  const { adopt } = useSession()
  const token = route.search.get('token')

  const [error, setError] = useState<string | null>(null)
  const [redeeming, setRedeeming] = useState(Boolean(token))
  const started = useRef(false)

  useEffect(() => {
    if (!token || started.current) return
    started.current = true
    redeemOnce('invite', token)
      .then((session) => {
        adopt(session)
        const first = session.workspaces?.[0]
        navigate(
          first ? buildPath('workspace', { workspaceId: first.id }) : ROUTES.home,
          { replace: true },
        )
      })
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => setRedeeming(false))
  }, [token, adopt])

  return (
    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-6">
      <h1 className="font-display text-[32px] leading-tight font-semibold">{t('invite.title')}</h1>

      {redeeming ? (
        <p role="status" className="flex items-center gap-2 text-[14px] text-muted">
          <Icon icon={LoaderCircle} className="motion-safe:animate-spin" />
          {t('invite.redeeming')}
        </p>
      ) : null}

      {!token ? (
        <div role="alert" className="rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-ink-2">
          {t('invite.noToken')}
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          data-testid="invite-error"
          className="flex flex-col gap-2 rounded-sm border border-signal-red/30 bg-signal-red-soft px-4 py-3"
        >
          <p className="flex items-center gap-2 text-[14px] font-medium text-signal-red-text">
            <Icon icon={CircleAlert} />
            {t('invite.failedTitle')}
          </p>
          <p className="text-[13px] text-signal-red-text">{error}</p>
          <p className="text-[13px] text-ink-2">{t('invite.failedNext')}</p>
        </div>
      ) : null}

      {!redeeming ? (
        <Button variant="secondary" className="self-start" onClick={() => navigate(ROUTES.signIn)}>
          <Icon icon={LogIn} />
          {t('invite.toSignIn')}
        </Button>
      ) : null}
    </div>
  )
}
